import { useState } from "react";
import { Button } from "@design-system/Button/Button";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { BulkSelectionControl } from "@design-system/BulkSelectionControl/BulkSelectionControl";
import { HStack } from "@design-system/HStack/HStack";
import { ClientAssignmentModal } from "./ClientAssignmentModal";
import { EntityAssignmentModal } from "./EntityAssignmentModal";
import { SelectedAssignmentTargets } from "./selectedAssignmentTargets";
import { assignmentCapReason } from "./useCommitAssignments";

/** Which field the modal was opened to set, when it is open. */
type AssignmentScope = "client" | "entity";

interface PostConnectBulkActionsProps {
    selectedCount: number;
    assignmentTargets: SelectedAssignmentTargets;
    onClearSelection: () => void;
}

/**
 * Client and entity assignment for a selection inside the post-connect modal.
 *
 * The table's own bulk bar additionally sends reconnect reminders; nothing here
 * is disconnected — these accounts were connected seconds ago — so this bar
 * carries only the two assignment actions. Both only stage: the modal commits
 * the whole batch through its own Confirm.
 */
export const PostConnectBulkActions = ({
    selectedCount,
    assignmentTargets,
    onClearSelection,
}: PostConnectBulkActionsProps) => {
    const [assignmentScope, setAssignmentScope] =
        useState<AssignmentScope | null>(null);

    const {
        entityAccountIds,
        clientAccountIds,
        lockedWorkspaceId,
        sharedEntityId,
    } = assignmentTargets;
    const clientBlockedReason =
        assignmentTargets.clientBlockedReason ??
        assignmentCapReason(clientAccountIds.length);
    const entityBlockedReason =
        assignmentTargets.blockedReason ??
        assignmentCapReason(entityAccountIds.length);

    const openAssignment = (scope: AssignmentScope) => () => {
        const blockedReason =
            scope === "client" ? clientBlockedReason : entityBlockedReason;
        if (blockedReason !== undefined) {
            return;
        }
        setAssignmentScope(scope);
    };

    return (
        <>
            <HStack gap={12} align="center">
                <BulkSelectionControl
                    selectedCount={selectedCount}
                    onClearSelection={onClearSelection}
                    size="md"
                />
            </HStack>
            <HStack gap={12} align="center">
                <Tooltip
                    key={clientBlockedReason}
                    content={clientBlockedReason}
                >
                    <Button
                        variant="primary"
                        size="md"
                        onClick={openAssignment("client")}
                        disabled={clientBlockedReason !== undefined}
                    >
                        Select client ({clientAccountIds.length})
                    </Button>
                </Tooltip>
                <Tooltip
                    key={entityBlockedReason}
                    content={entityBlockedReason}
                >
                    <Button
                        variant="primary"
                        size="md"
                        onClick={openAssignment("entity")}
                        disabled={entityBlockedReason !== undefined}
                    >
                        Select entity ({entityAccountIds.length})
                    </Button>
                </Tooltip>
            </HStack>
            {assignmentScope === "client" && (
                // Picking the client hands over to the entity step itself, so
                // a bulk client pick lands the same assignment a row does.
                <ClientAssignmentModal
                    accountIds={clientAccountIds}
                    initialWorkspaceId={lockedWorkspaceId}
                    onClose={() => setAssignmentScope(null)}
                />
            )}
            {assignmentScope === "entity" && lockedWorkspaceId !== null && (
                <EntityAssignmentModal
                    accountIds={entityAccountIds}
                    workspaceId={lockedWorkspaceId}
                    initialEntityId={sharedEntityId}
                    onClose={() => setAssignmentScope(null)}
                />
            )}
        </>
    );
};
